import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2 } from 'lucide-react'
import { useCheckInSend } from '../context'
import { useAudio } from '../../../../../lib/use-audio'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import {
  InsurerResultList,
  InsurerSearchField,
  useInsurerSearch,
} from './insurer-search'

const REGISTER_INSURER_INPUT_ID = 'frontdesk-register-insurer-search-input'

/**
 * Patient registration insurer step: find the statutory insurer in the
 * catalogue. Uses the same search field and result list as the coverage flow
 * and offers the reception when the insurer cannot be found (KC-81).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PatientRegisterInsurerStep />
 * ```
 */
export function PatientRegisterInsurerStep() {
  const { t } = useTranslation()
  const send = useCheckInSend()
  const search = useInsurerSearch()

  // Play audio on mount
  useAudio('checkIn-patientRegisterInsurer')

  useEffect(function focusInsurerSearch() {
    document.getElementById(REGISTER_INSURER_INPUT_ID)?.focus()
  }, [])

  const receptionAction = {
    label: t('self-service.help-reception'),
    icon: Building2,
    onClick: () => send({ type: 'RECEPTION_HELP' }),
    testId: 'frontdesk-register-insurer-reception-btn',
  }

  return (
    <KioskStep
      width="narrow"
      title={t('self-service.check-in.insurer-search.title')}
      lead={t('self-service.check-in.register-insurer.subtitle')}
      secondaryAction={receptionAction}
    >
      <InsurerSearchField
        id={REGISTER_INSURER_INPUT_ID}
        testId="frontdesk-register-insurer-search-input"
        search={search}
      />

      {search.failed ? (
        <KioskStatePanel
          tone="error"
          title={t('self-service.check-in.insurer-search.search-error-v2')}
          testId="frontdesk-register-insurer-error"
        />
      ) : null}

      {search.results.length > 0 ? (
        <InsurerResultList
          results={search.results}
          testIdPrefix="insurer-result"
          onSelect={(insurer) =>
            send({ type: 'INSURER_SELECTED', insurerId: insurer.iknr, insurerName: insurer.name })
          }
        />
      ) : null}

      {search.hasSearched && search.results.length === 0 ? (
        <KioskStatePanel
          tone="neutral"
          title={t('self-service.check-in.insurer-search.no-results')}
          body={t('self-service.check-in.insurer-search.no-results-hint')}
        />
      ) : null}
    </KioskStep>
  )
}
